/*
 * BuroEcl
 * Copyright (c) 2023 Buroclic. All rights reserved.
 * 
 * This file corresponds to the component allowing us to display
 * a JSX element inside a popup as a dialog (whether modal or not)
 */

// Imports
import React, { type MouseEventHandler, type ReactNode, useState, JSX } from "react";
import { TEModal, TEModalDialog, TEModalContent, TEModalHeader, TEModalBody, TEModalFooter } from "tw-elements-react";

type Props = {
  className?: string;
  visible: boolean;
  modal?: boolean;
  title: string;
  closeLabel?: string;
  closeHandler: MouseEventHandler<HTMLButtonElement>;
  confirmLabel?: string;
  confirmHandler: MouseEventHandler<HTMLButtonElement>;
  children: ReactNode;
};

export function PopUp(props: Props): JSX.Element {
  const className = props.className && props.className !== '' ? props.className : '';

  const [showPopup, setShowPopup] = useState(props.visible);
  const isModal = props.modal ? props.modal : false;

  return (
    <div className={`popup z-50 absolute h-full top-0 left-0 w-full ${!showPopup ? 'hidden' : ''}`}>
      {/* <!-- Modal --> */}
      <TEModal show={showPopup} setShow={setShowPopup} staticBackdrop={isModal} scrollable className={`${isModal ? 'darken-bg' : ''}`}>
        <TEModalDialog className={`${className}`}>
          <TEModalContent className="popup-window">
            <TEModalHeader className="sm-header">

              {/* <!--Modal title--> */}
              <h5 className="text-xl font-medium leading-normal text-neutral-800 dark:text-neutral-200">{props.title}</h5>

              {/* <!--Close button--> */}
              <button type="button"
                className="box-content rounded-none border-none hover:no-underline hover:opacity-75 focus:opacity-100 focus:shadow-none focus:outline-none"
                onClick={props.closeHandler} aria-label="Close">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="h-6 w-6" >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </TEModalHeader>
            {/* <!--Modal body--> */}
            <TEModalBody className="max-h-[80vh]"><React.Fragment>{props.children}</React.Fragment></TEModalBody>
            <TEModalFooter className="popup-btn-group w-full justify-end">
              <button type="button" onClick={props.closeHandler}
                className="inline-block rounded bg-red-300 mx-2 px-6 pb-2 pt-2.5 text-xs font-medium uppercase leading-normal text-primary-700 transition duration-150 ease-in-out hover:bg-primary-accent-100 focus:bg-primary-accent-100 focus:outline-none focus:ring-0 active:bg-primary-accent-200">
                {props.closeLabel ? props.closeLabel : 'Annuler'}
              </button>
              <button type="button" onClick={props.confirmHandler}
                className="ml-1 inline-block rounded bg-green-300 mx-2 px-6 pb-2 pt-2.5 text-xs font-medium uppercase leading-normal shadow-[0_4px_9px_-4px_#3b71ca] transition duration-150 ease-in-out hover:bg-primary-600 hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:bg-primary-600 focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:outline-none focus:ring-0 active:bg-primary-700 active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] dark:shadow-[0_4px_9px_-4px_rgba(59,113,202,0.5)] dark:hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] dark:focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] dark:active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)]" >
                {props.confirmLabel ? props.confirmLabel : 'Sauvegarder'}
              </button>
            </TEModalFooter>
          </TEModalContent>
        </TEModalDialog>
      </TEModal>
    </div>
  );
}